import React, { useState, useEffect, useMemo, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";
import { ASUHeader } from "@asu/component-header-footer";
import { Footer } from "../Footer";

import desktopLogo from "../../../assets/img/arizona-state-university-logo-vertical.png";
import mobileLogo from "../../../assets/img/arizona-state-university-logo.png";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Cookies from "js-cookie";

import parseHtml from "html-react-parser";
import { stripHtml } from "string-strip-html";
import { isInternalLink } from "is-internal-link";

import ErrorPage from "../ErrorPage";

const DEFAULT_ASU_HOST = "https://sites.thecollege.asu.edu";
const LOCAL_DEV_ASU_HOST = "http://fac22.asu.ddev.site";

const parseBooleanString = (value) => {
  if (value === "true") return true;
  if (value === "false") return false;
  return null;
};

const shouldUseLocalDrupalHost = () => {
  const envFlag = parseBooleanString(process.env.REACT_APP_USE_LOCAL_DRUPAL_HOST);
  if (envFlag !== null) return envFlag;

  if (typeof window !== "undefined") {
    try {
      const flag = parseBooleanString(
        window.localStorage?.getItem("clas:useLocalDrupalHost")
      );
      if (flag !== null) return flag;
    } catch (_error) {
      // ignore storage errors
    }

    const hostname = window.location.hostname.toLowerCase();
    if (
      hostname === "localhost" ||
      hostname === "127.0.0.1" ||
      hostname.endsWith(".local")
    ) {
      return true;
    }
  }
  return false;
};

const resolveAsuHost = () => {
  return shouldUseLocalDrupalHost() ? LOCAL_DEV_ASU_HOST : DEFAULT_ASU_HOST;
};

const ASU_HOST = resolveAsuHost();
const ASU_URL = (() => {
  try {
    return new URL(ASU_HOST);
  } catch (_error) {
    return new URL(DEFAULT_ASU_HOST);
  }
})();
const ASU_ORIGIN = ASU_URL.origin;

const isAbsoluteUrl = (value = "") =>
  /^https?:\/\//i.test(value) || value.startsWith("//");

const getWindowOrigin = () => {
  if (typeof window === "undefined") return "";
  try {
    if (window.location?.origin) return window.location.origin;
    if (window.location) {
      return `${window.location.protocol}//${window.location.host}`;
    }
  } catch (_error) {
    // ignore origin errors
  }
  return "";
};

const shouldProxyDrupalRequests = () => {
  if (typeof window === "undefined") return false;
  if (!ASU_ORIGIN) return false;
  if (getWindowOrigin() === ASU_ORIGIN) return false;
  return true;
};

const toDrupalPath = (value = "") => {
  if (!value) return "";
  const normalized = value.trim();
  if (!normalized) return "";

  if (normalized.startsWith("//")) {
    try {
      const temp = new URL(`${ASU_URL.protocol}${normalized}`);
      return `${temp.pathname}${temp.search}${temp.hash}`;
    } catch (_error) {
      return normalized.replace(/^\/+/, "/");
    }
  }

  if (isAbsoluteUrl(normalized)) {
    try {
      const temp = new URL(normalized);
      return `${temp.pathname}${temp.search}${temp.hash}`;
    } catch (_error) {
      return normalized;
    }
  }

  return normalized.startsWith("/") ? normalized : `/${normalized}`;
};

const resolveDrupalRequestUrl = (value = "") => {
  if (!value) return value;

  const normalizeAbsolute = (input) => {
    if (input.startsWith("//")) {
      return `${ASU_URL.protocol}${input}`;
    }
    return input;
  };

  const windowOrigin = getWindowOrigin() || ASU_ORIGIN;

  if (!isAbsoluteUrl(value)) {
    const sanitized = value.replace(/^\/+/, "");
    return `${ASU_ORIGIN}/${sanitized}`;
  }

  const absoluteUrl = normalizeAbsolute(value);
  try {
    const parsed = new URL(absoluteUrl, windowOrigin);
    if (
      windowOrigin &&
      parsed.origin === windowOrigin &&
      parsed.origin !== ASU_ORIGIN
    ) {
      return `${ASU_ORIGIN}${parsed.pathname}${parsed.search}${parsed.hash}`;
    }
    return parsed.href;
  } catch (_error) {
    return absoluteUrl;
  }
};

const normalizeDependencyPath = (value = "") => {
  if (!value) return "";
  let normalizedValue = value.trim();
  if (!normalizedValue) return "";

  if (normalizedValue.startsWith("//")) {
    normalizedValue = `https:${normalizedValue}`;
  }

  const marker = "/profiles/openclas/modules/custom/";
  const escapedMarker = marker.replace(/\//g, "\\/");
  const duplicatePattern = new RegExp(
    `${escapedMarker}([^/]+)${escapedMarker}\\1\\/`,
    "g"
  );

  let deduped = normalizedValue;
  let replaced = deduped.replace(duplicatePattern, `${marker}$1/`);

  while (replaced !== deduped) {
    deduped = replaced;
    replaced = deduped.replace(duplicatePattern, `${marker}$1/`);
  }

  return deduped;
};

const toAbsoluteUrl = (value = "") => {
  const normalizedValue = normalizeDependencyPath(value);
  if (!normalizedValue) return "";
  if (isAbsoluteUrl(normalizedValue)) return normalizedValue;
  return `${ASU_HOST}${
    normalizedValue.startsWith("/") ? "" : "/"
  }${normalizedValue}`;
};

const getGlobalOnceStore = () => {
  if (typeof window === "undefined") return null;
  window.__clasOnceStore = window.__clasOnceStore || new WeakMap();
  return window.__clasOnceStore;
};

const ensureDrupalProxying = () => {
  if (!shouldProxyDrupalRequests()) return;
  if (typeof window === "undefined") return;
  if (window.__clasDrupalProxyApplied) return;

  window.__clasDrupalProxyApplied = true;

  const originalFetch = window.fetch;
  if (typeof originalFetch === "function") {
    window.fetch = function proxiedFetch(resource, init) {
      if (typeof resource === "string") {
        const nextResource = resolveDrupalRequestUrl(resource);
        return originalFetch.call(this, nextResource, init);
      }
      if (resource && typeof resource.url === "string" && typeof Request === "function") {
        const rewritten = resolveDrupalRequestUrl(resource.url);
        if (rewritten !== resource.url) {
          return originalFetch.call(this, new Request(rewritten, resource), init);
        }
      }
      return originalFetch.call(this, resource, init);
    };
  }

  const OriginalXHR = window.XMLHttpRequest;
  if (
    OriginalXHR &&
    OriginalXHR.prototype &&
    typeof OriginalXHR.prototype.open === "function"
  ) {
    const originalOpen = OriginalXHR.prototype.open;
    OriginalXHR.prototype.open = function proxiedOpen(
      method,
      url,
      async,
      user,
      password
    ) {
      const rewrittenUrl = resolveDrupalRequestUrl(url);
      return originalOpen.call(this, method, rewrittenUrl, async, user, password);
    };
  }
};

const ensureJqueryOnce = () => {
  if (typeof window === "undefined") return;
  const $ = window.jQuery;
  if (!$ || !$.fn || typeof $.fn.once === "function") {
    return;
  }

  const onceStore = getGlobalOnceStore();
  if (!onceStore) return;

  const mark = (element, id) => {
    let seen = onceStore.get(element);
    if (!seen) {
      seen = new Set();
      onceStore.set(element, seen);
    }
    if (seen.has(id)) {
      return false;
    }
    seen.add(id);
    return true;
  };

  $.fn.once = function once(id) {
    if (!id) return this;
    const elements = [];
    this.each(function registerOnce() {
      if (mark(this, id)) {
        elements.push(this);
      }
    });
    return this.pushStack(elements);
  };

  $.fn.removeOnce = function removeOnce(id) {
    if (!id) return this;
    this.each(function unregisterOnce() {
      const seen = onceStore.get(this);
      if (!seen) return;
      seen.delete(id);
      if (!seen.size) {
        onceStore.delete(this);
      }
    });
    return this;
  };
};

const ensureDrupalEnvironment = () => {
  if (typeof window === "undefined") return;

  if (!window.drupalSettings) {
    window.drupalSettings = {};
  }

  window.drupalSettings.path = window.drupalSettings.path || {};
  if (!window.drupalSettings.path.baseUrl) {
    window.drupalSettings.path.baseUrl = `${ASU_ORIGIN}/`;
  }
  if (!window.drupalSettings.path.basePath) {
    window.drupalSettings.path.basePath = "/";
  }

  getGlobalOnceStore();

  window.Drupal = window.Drupal || {};
  window.Drupal.behaviors = window.Drupal.behaviors || {};
  window.Drupal.settings = window.Drupal.settings || window.drupalSettings;

  if (typeof window.Drupal.t !== "function") {
    window.Drupal.t = (str) => str;
  }

  if (typeof window.Drupal.formatPlural !== "function") {
    window.Drupal.formatPlural = (count, singular, plural) => {
      const template = count === 1 ? singular : plural;
      return template.replace("@count", count);
    };
  }

  if (typeof window.Drupal.detachBehaviors !== "function") {
    window.Drupal.detachBehaviors = () => {};
  }

  if (typeof window.Drupal.attachBehaviors !== "function") {
    window.Drupal.attachBehaviors = (context = document, settings = window.drupalSettings || {}) => {
      const behaviors = window.Drupal.behaviors || {};
      Object.keys(behaviors).forEach((key) => {
        const behavior = behaviors[key];
        if (behavior && typeof behavior.attach === "function") {
          try {
            behavior.attach(context, settings);
          } catch (error) {
            console.error(`Drupal behavior "${key}" failed to attach.`, error);
          }
        }
      });
    };
  }

  if (typeof window.once !== "function") {
    const onceStore = getGlobalOnceStore();
    window.once = (id, selector, context = document) => {
      const root = context && context.querySelectorAll ? context : document;
      const elements = Array.from(root.querySelectorAll(selector));
      return elements.filter((element) => {
        let seen = onceStore.get(element);
        if (!seen) {
          seen = new Set();
          onceStore.set(element, seen);
        }
        if (seen.has(id)) {
          return false;
        }
        seen.add(id);
        return true;
      });
    };
  }

  ensureDrupalProxying();
  ensureJqueryOnce();
};

const LayoutSection = styled.div`
  padding-top: 140px;

  @media (max-width: 991px) {
    padding-top: 120px;
  }
`;

function Content(props) {
  const history = useHistory();
  const location = useLocation();

  const [isAuthenticated, setAuthenticated] = useState(false);
  const [user, setUser] = useState(Cookies.get("username"));
  const [targetNID, setTargetNID] = useState();
  const [menu, setMenu] = useState(props.data.menu);
  const [isCustom, setCustom] = useState(props.custom);

  const handleNavClick = (e, item) => {
    e.preventDefault();
    e.stopPropagation();
    setTargetNID(item.nid);
    const path = isCustom ? item.alias : `/${props.group}${item.alias}`;
    history.push(path);
  };

  /*
  const findNavDestination = (target, menu) => {
    for (let i = 0; i < menu.length; i++) {
      const item = menu[i];

      // Recurse into submenus
      if (Array.isArray(item.items) && item.items.length) {
        const found = findNavDestination(target, item.items);
        if (found) return true;
      }

      // Match on title
      if (target === item.title) {
        setTargetNID(item.nid);
        const path = isCustom ? item.alias : `/${props.group}${item.alias}`;
        history.push(path);
        return true;
      }
    }
    return false;
  };
  */

  const buildNav = (items) => {
    let navTree = [];
    if (Array.isArray(items)) {
      for (let i = 0; i < items.length; i++) {
        if (items[i].hidden) continue; // skip hidden items
        if (Array.isArray(items[i].items)) {
          navTree.push({
            text: items[i].title,
            href: "#",
            items: [buildNav(items[i].items)],
          });
        } else {
          navTree.push(buildNavNode(items[i], isCustom, i === 0));
        }
      }
    }
    return navTree;
  };

  const aliasToNid = useMemo(() => {
    const map = new Map();
    const walk = (items = []) => {
      for (const it of items) {
        if (!it) continue;

        // index ALL internal items, hidden or not
        if (!Array.isArray(it.items) && isInternalLink(it.alias)) {
          const full = isCustom ? it.alias : `/${props.group}${it.alias}`;
          map.set(full, it.nid);
        }

        if (Array.isArray(it.items) && it.items.length) {
          walk(it.items);
        }
      }
    };
    if (Array.isArray(menu)) walk(menu);
      return map;
  }, [menu, isCustom, props.group]);

  const buildNavNode = (item, isCustom, isFirst) => {
    const title = item.title;
    const alias = item.alias;
    const isHidden = item.hidden === true;

    const path = decodeURIComponent(window.location.pathname);
    let isSelected = false;

    if (
      (isCustom && (path === alias || (isFirst && path === "/"))) ||
      (!isCustom &&
        (path === "/" + props.group + alias ||
          (isFirst && path === "/" + props.group)))
    ) {
      isSelected = true;
    }

    let navClass = "";
    if (isInternalLink(alias)) {
      navClass = isHidden ? "hidden" : "";
      return {
        onClick: (e) => handleNavClick(e, item),
        text: title,
        href: isCustom ? alias : "/" + props.group + alias,
        type: "",
        selected: isSelected,
        ...(navClass && { class: navClass })
      };
    } else {
      navClass = isHidden ? "externalLink hidden" : "externalLink";
      return {
        text: title,
        href: alias,
        class: navClass,
      };
    }
  };

  const activeContent = useMemo(() => {
    if (!Array.isArray(props.data.content)) return null;
    return props.data.content.find((item) => item && item.nid === targetNID) || null;
  }, [props.data.content, targetNID]);

  const content = useMemo(() => {
    if (!activeContent || !activeContent.content) return "";
    return parseHtml(activeContent.content);
  }, [activeContent]);

  useEffect(() => {
    if (typeof document === "undefined") return;

    const rewriteAttribute = (selector, attribute) => {
      const nodes = document.querySelectorAll(selector);
      nodes.forEach((node) => {
        const value = node.getAttribute(attribute);
        if (!value) return;
        const newValue = toDrupalPath(value);
        if (newValue && newValue !== value) {
          node.setAttribute(attribute, newValue);
        }
      });
    };

    rewriteAttribute("[data-feed]", "data-feed");
    rewriteAttribute("[data-feed-next]", "data-feed-next");
    rewriteAttribute("[data-news-feed]", "data-news-feed");
    rewriteAttribute("[data-events-feed]", "data-events-feed");
  }, [content]);

  const baseURL = !isCustom ? "/" + props.group + "/" : "/";

  const dependencySource =
    (activeContent && activeContent.dependencies) || props.data.dependencies;

  const cssDependencies = Array.isArray(dependencySource?.css)
    ? dependencySource.css
    : [];

  const jsDependencies = Array.isArray(dependencySource?.js)
    ? dependencySource.js
    : [];

  const contentKey = activeContent
    ? `clas-content-${activeContent.nid}`
    : "clas-content-empty";

  const normalizedCssDependencies = useMemo(() => {
    if (!cssDependencies.length) return [];
    return cssDependencies
      .map((href) => toAbsoluteUrl(href))
      .filter(Boolean);
  }, [cssDependencies]);

  const normalizedJsDependencies = useMemo(() => {
    if (!jsDependencies.length) return [];
    return jsDependencies.map((src) => toAbsoluteUrl(src)).filter(Boolean);
  }, [jsDependencies]);

  const dependencySignature = useMemo(() => {
    if (!normalizedCssDependencies.length && !normalizedJsDependencies.length) {
      return "";
    }
    return [
      contentKey,
      normalizedCssDependencies.join("|"),
      normalizedJsDependencies.join("|"),
    ].join("::");
  }, [contentKey, normalizedCssDependencies, normalizedJsDependencies]);

  const lastDependencySignatureRef = useRef("");

  useEffect(() => {
    if (!dependencySignature) {
      lastDependencySignatureRef.current = "";
    }
  }, [dependencySignature]);

  useEffect(() => {
    ensureDrupalEnvironment();
  }, []);

  const setExternalTargets = () => {
    const links = Array.from(document.querySelectorAll("a.externalLink"));
    links.forEach((element) => {
      element.setAttribute("target", "_blank");
    });
  };

  useEffect(() => {
    const path = decodeURIComponent(location.pathname);
    const rootPath = isCustom ? "/" : `/${props.group}`;

    // 1) Direct alias match wins (hidden allowed)
    const matchedNid = aliasToNid.get(path);
    if (matchedNid && matchedNid !== targetNID) {
      setTargetNID(matchedNid);
      return;
    }

    // 2) Root-only fallback → first VISIBLE internal page
    if (path === rootPath) {
      const firstVisible = (() => {
        const find = (items = []) => {
          for (const it of items) {
            if (!it || it.hidden) continue;                 // skip hidden in fallback
            if (Array.isArray(it.items) && it.items.length) {
              const nid = find(it.items);
              if (nid) return nid;
            } else if (isInternalLink(it.alias)) {
              return it.nid;                                // first visible internal page
            }
          }
          return null;
        };
        return Array.isArray(menu) ? find(menu) : null;
      })();

      if (firstVisible && firstVisible !== targetNID) {
        setTargetNID(firstVisible);
      }
    }
  }, [location.pathname, aliasToNid, menu, isCustom, props.group, targetNID]);

  useEffect(() => {
    setExternalTargets();
  }, []);

  useEffect(() => {
    document.title = props.data.site_name;
  }, [props.data.site_name]);

  useEffect(() => {
    if (!dependencySignature) {
      return undefined;
    }

    if (lastDependencySignatureRef.current === dependencySignature) {
      return undefined;
    }

    let cancelled = false;
    const cleanupFns = [];

    const loadDependencies = () => {
      if (cancelled) return;

      lastDependencySignatureRef.current = dependencySignature;
      ensureDrupalEnvironment();

      const dispatchLifecycleEvents = () => {
        try {
          document.dispatchEvent(new Event("DOMContentLoaded"));
        } catch (_error) {
          // ignore
        }
        try {
          window.dispatchEvent(new Event("load"));
        } catch (_error) {
          // ignore
        }
      };

      const triggerDrupalBehaviors = () => {
        if (
          typeof window !== "undefined" &&
          window.Drupal &&
          typeof window.Drupal.attachBehaviors === "function"
        ) {
          try {
            window.Drupal.attachBehaviors(document, window.drupalSettings || {});
          } catch (error) {
            console.error("Drupal behaviors failed to attach.", error);
          }
        }
      };

      normalizedCssDependencies.forEach((href) => {
        try {
          const selector = `link[data-dynamic-dependency="${href}"]`;
          if (document.querySelector(selector)) return;

          const linkEl = document.createElement("link");
          linkEl.rel = "stylesheet";
          linkEl.href = href;
          linkEl.dataset.dynamicDependency = href;
          document.head.appendChild(linkEl);
        } catch (error) {
          console.error(`Failed to load CSS dependency: ${href}`, error);
        }
      });

      const loadScriptsSequentially = (sources) => {
        if (!sources.length) {
          triggerDrupalBehaviors();
          return;
        }

        const loadNext = (index) => {
          if (cancelled) return;
          if (index >= sources.length) {
            triggerDrupalBehaviors();
            return;
          }

          const src = sources[index];
          const selector = `script[data-dynamic-dependency="${src}"]`;
          const existingScript = document.querySelector(selector);
          if (existingScript) {
            existingScript.remove();
          }

          const loadScript = (scriptSrc) => {
            let scriptEl;
            try {
              scriptEl = document.createElement("script");
              scriptEl.src = scriptSrc;
              scriptEl.async = false;
              scriptEl.defer = false;
              scriptEl.dataset.dynamicDependency = src;

              let timeoutId;

              const cleanup = () => {
                scriptEl.removeEventListener("load", handleLoad);
                scriptEl.removeEventListener("error", handleError);
                if (timeoutId) {
                  window.clearTimeout(timeoutId);
                  timeoutId = null;
                }
              };

              const handleLoad = () => {
                cleanup();
                dispatchLifecycleEvents();
                triggerDrupalBehaviors();
                loadNext(index + 1);
              };

              const handleError = (event) => {
                cleanup();
                console.error(`Failed to load dependency: ${scriptSrc}`, event?.error || event);
                loadNext(index + 1);
              };

              timeoutId = window.setTimeout(() => {
                handleError(new Error(`Timed out loading dependency: ${scriptSrc}`));
              }, 5000);

              scriptEl.addEventListener("load", handleLoad);
              scriptEl.addEventListener("error", handleError);
              document.body.appendChild(scriptEl);
            } catch (error) {
              console.error(`Failed to append dependency: ${scriptSrc}`, error);
              loadNext(index + 1);
            }
          };

          try {
            loadScript(src);
          } catch (error) {
            console.error(`Unexpected error while loading dependency: ${src}`, error);
            loadNext(index + 1);
          }
        };

        loadNext(0);
      };

      loadScriptsSequentially(normalizedJsDependencies);
    };

    const runWhenReady = () => {
      if (document.readyState === "complete") {
        loadDependencies();
      } else {
        const handleWindowLoad = () => {
          window.removeEventListener("load", handleWindowLoad);
          loadDependencies();
        };
        window.addEventListener("load", handleWindowLoad);
        cleanupFns.push(() =>
          window.removeEventListener("load", handleWindowLoad)
        );
      }
    };

    runWhenReady();

    return () => {
      cancelled = true;
      while (cleanupFns.length) {
        const fn = cleanupFns.pop();
        if (typeof fn === "function") fn();
      }
    };
  }, [
    dependencySignature,
    normalizedCssDependencies,
    normalizedJsDependencies,
  ]);

  const nav = buildNav(menu);

  const headerTitle = props.data.site_name || "Faculty Sites";
  const headerLogo = {
    alt: "Arizona State University",
    title: "Arizona State University",
    src: desktopLogo,
    mobileSrc: mobileLogo,
    brandLink: "https://asu.edu"
  };

  let redirect = false;

  if (Array.isArray(props.data.redirects)) {
    props.data.redirects.forEach((i) => {
      if (props.page === i.path) {
        redirect = true;
        window.location.replace(i.target);
      }
    });
  }

  return (
    <div>
      <ASUHeader
        isPartner={false}
        navTree={nav}
        mobileNavTree={nav}
        title={headerTitle}
        baseUrl={baseURL}
        logo={headerLogo}
        loggedIn={isAuthenticated}
        userName={user || ""}
        loginLink="/cas"
        logoutLink="/caslogout"
        buttons={[]}
        breakpoint="Lg"
        animateTitle={false}
        expandOnHover={false}
        searchUrl="https://search.asu.edu/search"
        site={props.group || "faculty-sites"}
      />

      <LayoutSection>
        {props.status === 500 ? (
          <ErrorPage {...props} error="" custom={isCustom} />
        ) : !activeContent ? (
          redirect ? (
            <ErrorPage {...props} error="304" custom={isCustom} />
          ) : props.page === undefined ? (
            <ErrorPage {...props} error="500" custom={isCustom} />
          ) : (
            <ErrorPage {...props} error="404" custom={isCustom} />
          )
        ) : (
          <div className="main-container" key={contentKey}>
            {content}
          </div>
        )}
        <Footer siteName={headerTitle} socials={props.data.socials} />
      </LayoutSection>
    </div>
  );
}

export { Content };
